var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import axios from 'axios';
import { injectable, inject, Store } from '@servicetitan/react-ioc';
import { reaction } from 'mobx';
import { ConnectionService } from './connection-service';
import { LOG_CONFIG_TOKEN, Log } from './log';
import { LogStorage, lastId } from './log-storage';
const SECOND = 1000;
const DEFAULT_SEND_INTERVAL = 60 * SECOND;
const INITIAL_RETRY_INTERVAL = SECOND;
const MAX_RETRY_INTERVAL = INITIAL_RETRY_INTERVAL * 512;
const BATCH_SIZE = 128;
let LogSender = class LogSender extends Store {
    constructor(connectionService, logStorage, logConfig, log) {
        super();
        Object.defineProperty(this, "connectionService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: connectionService
        });
        Object.defineProperty(this, "logStorage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: logStorage
        });
        Object.defineProperty(this, "logConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: logConfig
        });
        Object.defineProperty(this, "log", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: log
        });
        Object.defineProperty(this, "inSendFailedScenario", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "onItemsChangedDisposer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sendInterval", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_SEND_INTERVAL
        });
        Object.defineProperty(this, "intervalId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.setRegularSendScenario();
    }
    dispose() {
        window.clearInterval(this.intervalId);
    }
    onItemsChanged(items) {
        if (items.length >= BATCH_SIZE) {
            this.send();
        }
    }
    setSendInterval(interval) {
        this.sendInterval = interval;
        window.clearInterval(this.intervalId);
        this.intervalId = window.setInterval(() => {
            if (this.logStorage.items.length > 0) {
                this.send();
            }
        }, interval);
    }
    send() {
        // If offline, punt
        if (!this.connectionService.online) {
            return;
        }
        /*
         * Using Web Locks API to prevent multiple tabs from simultaneously
         * sending previously saved items. If the API isn't available, ¯\_(ツ)_/¯
         */
        if (!('locks' in globalThis.navigator)) {
            this.sendEvents();
            return;
        }
        // eslint-disable-next-line no-restricted-globals
        navigator.locks.request(this.logConfig.storageKey, { ifAvailable: true }, async (lock) => {
            if (lock) {
                await this.sendEvents();
            }
        });
    }
    async sendEvents() {
        if (this.isSending) {
            return;
        }
        this.isSending = true;
        try {
            const logEvents = this.logStorage.loadBatch(BATCH_SIZE);
            if (logEvents.length) {
                await axios.post(this.logConfig.endpoint, logEvents);
                this.logStorage.deleteToId(lastId(logEvents));
                if (this.inSendFailedScenario) {
                    this.setRegularSendScenario();
                }
            }
        }
        catch (e) {
            this.setFailedSendScenario();
            /* istanbul ignore next */
            if (process.env.NODE_ENV !== 'test') {
                throw e;
            }
        }
        finally {
            this.isSending = false;
        }
    }
    setRegularSendScenario() {
        this.inSendFailedScenario = false;
        this.setSendInterval(DEFAULT_SEND_INTERVAL);
        this.onItemsChangedDisposer = reaction(() => this.logStorage.items, items => this.onItemsChanged(items));
    }
    setFailedSendScenario() {
        var _a;
        if (this.inSendFailedScenario) {
            this.increaseSendInterval();
            return;
        }
        this.inSendFailedScenario = true;
        this.setSendInterval(INITIAL_RETRY_INTERVAL);
        (_a = this.onItemsChangedDisposer) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    increaseSendInterval() {
        if (this.sendInterval < MAX_RETRY_INTERVAL) {
            this.setSendInterval(this.sendInterval * 2);
        }
    }
};
LogSender = __decorate([
    injectable(),
    __param(0, inject(ConnectionService)),
    __param(1, inject(LogStorage)),
    __param(2, inject(LOG_CONFIG_TOKEN)),
    __param(3, inject(Log)),
    __metadata("design:paramtypes", [ConnectionService,
        LogStorage, Object, Log])
], LogSender);
export { LogSender };
//# sourceMappingURL=log-sender.js.map